import {partition} from '@benchling/js-utils';

import {Sequence} from 'biojs/Sequence';
import {
  CircularFragmentResultData,
  getCircularHomologyStripeData,
} from 'molbio/visualization/FragmentVisualizationUtil';
import {Angle} from 'util/geometry/GeometryConstants';
import {getAngles} from 'util/geometry/GeometryUtil';

export interface HomologyAwareFragmentResultData extends CircularFragmentResultData {
  /** If provided, what region to select on the sequence when the piece is double-clicked */
  selectRegionOnDoubleClick?: {start: number; end: number};
}

/**
 * Calculates what colors to draw at what locations for all the homology assembly fragment results on a
 * sequence.
 * @param sequence A BioJS Sequence Backbone entity.
 * @param outerRadius The outer radius of the circular view in pixels.
 * @param getAngle A function that takes in a base pair index and returns the angle in radians at which that
 *                 base pair should be drawn.
 */
export function getHomologyAwareFragmentResultData(
  sequence: Sequence,
  outerRadius: number,
  getAngle: (basePair: number) => Angle
): Array<HomologyAwareFragmentResultData> {
  const sequenceLength = sequence.length();
  const [nonHomologyFragmentResults, homologyFragmentResults] = partition(
    sequence.fragmentResults().models,
    (fragmentResult) => fragmentResult.homologyRegionOtherColor() == null
  );
  // We want to draw non-homology regions first and then homology regions, in case the homology regions
  // need to overlap the non-homology regions.
  const nonHomologyFragmentData = nonHomologyFragmentResults.map((fragmentResult) => {
    const {startAngle, endAngle} = getAngles(
      fragmentResult.start()!,
      fragmentResult.end(),
      sequenceLength,
      (basePair) => getAngle(basePair)
    );
    return {
      pieceStartAngle: startAngle,
      pieceEndAngle: endAngle,
      color: fragmentResult.color()!,
    };
  });
  const homologyFragmentData = homologyFragmentResults.flatMap((fragmentResult) =>
    getCircularHomologyStripeData(
      {
        start: fragmentResult.start()!,
        end: fragmentResult.end()!,
        color: fragmentResult.color()!,
        homologyRegionOtherColor: fragmentResult.homologyRegionOtherColor()!,
      },
      sequenceLength,
      outerRadius,
      getAngle
    ).map((homologyStripeDatum) => ({
      ...homologyStripeDatum,
      selectRegionOnDoubleClick: {start: fragmentResult.start()!, end: fragmentResult.end()!},
    }))
  );
  return [...nonHomologyFragmentData, ...homologyFragmentData];
}
